import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { ActiveGenerationJob, CompanyGenerationUpdate } from "@signalist/shared-types";

import { RootState } from "@/redux";

import {
	CopywritingGenerationQueuedEvent,
	CopywritingStage,
	FrameworkOverrides,
	GenerateCopywritingResponse,
} from "../../pages/Messages/components/GenerateBar/types";
import { copywritingOrchestrationApi } from "./copywritingOrchestration.api";

/**
 * One live copywriting job of the company job map (PREC-3635 P6 D1): the
 * `GET /active` wire shape plus the transient RUNNING stage label pushed by
 * the company-room socket.
 */
export type GenerationJob = ActiveGenerationJob & { stage?: string };

/**
 * Where a single-lead generation stands (PREC-4052): waiting for a slot in the company's lane, or
 * picked up by a worker (the first stage event). Both used to read as one spinner.
 */
export type GenerationPhase = "queued" | "running";

/** Whether a generation error is a refusal to spend: "Try again" cannot succeed until a top-up. */
export const isCreditsRefusalMessage = (message: string | null | undefined): boolean =>
	/insufficient credits|not_enough_credits|not enough credits/i.test(message ?? "");

interface CopywritingOrchestrationState {
	isGenerating: boolean;
	currentStage: CopywritingStage | null;
	currentStageLabel: string | null;
	generationResult: GenerateCopywritingResponse | null;
	error: string | null;
	freshStartOverride: boolean;
	frameworkOverrides: FrameworkOverrides;
	eventId: number | null;
	listPersonId: number | null;
	alreadyInProgress: boolean;
	/** Queued or running, while `isGenerating`; null before the request is accepted. */
	generationPhase: GenerationPhase | null;
	/** When the current phase began (epoch ms), for the elapsed time beside it. */
	phaseSince: number | null;
	/** The current `error` is a refusal to spend (shown as a credits message, not "Try again"). */
	errorIsCredits: boolean;
	/**
	 * Company-wide job map (P6 D1): hydrated from `GET /active`, kept fresh by
	 * `applyCompanyGenerationUpdate`. Feeds the AI queue pill and the per-card /
	 * per-signal-row AI states; independent of the single-lead fields above.
	 */
	jobsByProspectId: Record<number, GenerationJob>;
}

const initialState: CopywritingOrchestrationState = {
	isGenerating: false,
	currentStage: null,
	currentStageLabel: null,
	generationResult: null,
	error: null,
	freshStartOverride: false,
	frameworkOverrides: {
		step1FrameworkId: null,
		step2FrameworkId: null,
		step3FrameworkId: null,
	},
	eventId: null,
	listPersonId: null,
	alreadyInProgress: false,
	generationPhase: null,
	phaseSince: null,
	errorIsCredits: false,
	jobsByProspectId: {},
};

const clearPhase = (state: CopywritingOrchestrationState) => {
	state.generationPhase = null;
	state.phaseSince = null;
};

export const copywritingOrchestrationSlice = createSlice({
	name: "copywritingOrchestration",
	initialState,
	reducers: {
		setGenerating(state, action: PayloadAction<boolean>) {
			state.isGenerating = action.payload;

			if (action.payload) {
				state.error = null;
				state.errorIsCredits = false;
				state.generationResult = null;
				state.currentStage = null;
				state.currentStageLabel = null;
				state.alreadyInProgress = false;
			} else {
				clearPhase(state);
			}
		},
		/**
		 * A lead re-opened while its generation is queued or running (PREC-4052): the persisted status
		 * says which, and `since` (the lead's last update) how long it has been in that state.
		 */
		hydrateGenerationPhase(
			state,
			action: PayloadAction<{ phase: GenerationPhase; since: number | null }>
		) {
			state.isGenerating = true;
			state.error = null;
			state.errorIsCredits = false;
			state.generationResult = null;
			state.currentStage = null;
			state.currentStageLabel = null;
			state.alreadyInProgress = false;
			state.generationPhase = action.payload.phase;
			state.phaseSince = action.payload.since;
		},
		/** A stage event: the first one means a worker picked the job up (queued → running). */
		setStageUpdate(
			state,
			action: PayloadAction<{ stage: CopywritingStage; label: string; receivedAt?: number }>
		) {
			state.currentStage = action.payload.stage;
			state.currentStageLabel = action.payload.label;

			if (state.generationPhase !== "running") {
				state.generationPhase = "running";
				state.phaseSince = action.payload.receivedAt ?? state.phaseSince;
			}
		},
		setGenerationQueued(
			state,
			action: PayloadAction<CopywritingGenerationQueuedEvent & { receivedAt?: number }>
		) {
			state.isGenerating = true;
			state.eventId = action.payload.eventId;
			state.listPersonId = action.payload.listPersonId;
			state.error = null;
			state.errorIsCredits = false;
			state.generationResult = null;
			state.alreadyInProgress = false;

			if (state.generationPhase !== "running") {
				state.generationPhase = "queued";
				state.phaseSince = action.payload.receivedAt ?? state.phaseSince;
			}
		},
		setGenerationResult(state, action: PayloadAction<GenerateCopywritingResponse>) {
			state.generationResult = action.payload;
			state.isGenerating = false;
			state.currentStage = null;
			state.currentStageLabel = null;
			state.alreadyInProgress = false;
			clearPhase(state);
		},
		setGenerationError(state, action: PayloadAction<string>) {
			state.error = action.payload;
			state.errorIsCredits = isCreditsRefusalMessage(action.payload);
			state.isGenerating = false;
			state.currentStage = null;
			state.currentStageLabel = null;
			state.alreadyInProgress = false;
			clearPhase(state);
		},
		setFreshStartOverride(state, action: PayloadAction<boolean>) {
			state.freshStartOverride = action.payload;
		},
		setFrameworkOverrides(state, action: PayloadAction<FrameworkOverrides>) {
			state.frameworkOverrides = action.payload;
		},
		clearFrameworkOverrides(state) {
			state.frameworkOverrides = {
				step1FrameworkId: null,
				step2FrameworkId: null,
				step3FrameworkId: null,
			};
		},
		/**
		 * Compact company-room socket update onto the job map (P6 D1/D2). `FAILED`
		 * removes the job (errors surface per-prospect, never in the pill); an
		 * update for a prospect not in the map is ignored — the payload carries no
		 * display fields, so the socket hook refetches `GET /active` instead.
		 */
		applyCompanyGenerationUpdate(state, action: PayloadAction<CompanyGenerationUpdate>) {
			const update = action.payload;

			if (update.status === "FAILED") {
				delete state.jobsByProspectId[update.prospectId];

				return;
			}

			const job = state.jobsByProspectId[update.prospectId];

			if (!job) {
				return;
			}

			job.status = update.status;
			job.listPersonId = update.listPersonId;
			job.eventId = update.eventId;

			if (update.seedSignalId !== undefined) {
				job.seedSignalId = update.seedSignalId;
			}

			job.stage = update.status === "RUNNING" ? update.stage : undefined;
		},
		/** Optimistic ready-chip removal on review-now (P6 D1/D6). */
		markJobReviewed(state, action: PayloadAction<number>) {
			delete state.jobsByProspectId[action.payload];
		},
		resetGeneration(state) {
			state.isGenerating = false;
			state.currentStage = null;
			state.currentStageLabel = null;
			state.generationResult = null;
			state.error = null;
			state.freshStartOverride = false;
			state.frameworkOverrides = {
				step1FrameworkId: null,
				step2FrameworkId: null,
				step3FrameworkId: null,
			};
			state.eventId = null;
			state.listPersonId = null;
			state.alreadyInProgress = false;
			state.errorIsCredits = false;
			clearPhase(state);
		},
	},
	extraReducers: (builder) => {
		// Hydrate-all (P6 D1): `GET /active` replaces the job map wholesale. A
		// still-RUNNING job keeps its socket-pushed stage label across refetches.
		builder.addMatcher(
			copywritingOrchestrationApi.endpoints.getActiveGenerations.matchFulfilled,
			(state, { payload }) => {
				const next: Record<number, GenerationJob> = {};

				for (const job of payload.data ?? []) {
					const stage = state.jobsByProspectId[job.prospectId]?.stage;

					next[job.prospectId] = job.status === "RUNNING" && stage ? { ...job, stage } : job;
				}

				state.jobsByProspectId = next;
			}
		);
		builder.addMatcher(
			copywritingOrchestrationApi.endpoints.generateCopywriting.matchPending,
			(state) => {
				state.isGenerating = true;
				state.error = null;
				state.errorIsCredits = false;
				state.generationResult = null;
				state.alreadyInProgress = false;
				clearPhase(state);
			}
		);
		builder.addMatcher(
			copywritingOrchestrationApi.endpoints.generateCopywriting.matchFulfilled,
			(state, { payload, meta }) => {
				state.eventId = payload.eventId;
				state.listPersonId = payload.listPersonId;
				state.isGenerating = true;

				// Accepted = queued in the company's lane, until the first stage event (PREC-4052).
				if (state.generationPhase !== "running") {
					state.generationPhase = "queued";
					state.phaseSince = meta.fulfilledTimeStamp;
				}
			}
		);
		builder.addMatcher(
			copywritingOrchestrationApi.endpoints.generateCopywriting.matchRejected,
			(state, action) => {
				const payload = action.payload as
					| { status?: number; data?: { message?: string } }
					| undefined;

				if (payload?.status === 409) {
					state.alreadyInProgress = true;
					state.error = payload?.data?.message || "Generation already in progress";

					return;
				}

				state.error = payload?.data?.message || "Generation failed";
				state.errorIsCredits = payload?.status === 402 || isCreditsRefusalMessage(state.error);
				state.isGenerating = false;
				state.currentStage = null;
				state.currentStageLabel = null;
				clearPhase(state);
			}
		);
		builder.addMatcher(
			copywritingOrchestrationApi.endpoints.generateBatchCopywriting.matchPending,
			(state) => {
				state.isGenerating = true;
				state.error = null;
				state.generationResult = null;
				state.alreadyInProgress = false;
			}
		);
		builder.addMatcher(
			copywritingOrchestrationApi.endpoints.generateBatchCopywriting.matchFulfilled,
			(state) => {
				state.isGenerating = false;
			}
		);
		builder.addMatcher(
			copywritingOrchestrationApi.endpoints.generateBatchCopywriting.matchRejected,
			(state, action) => {
				const payload = action.payload as
					| { status?: number; data?: { message?: string } }
					| undefined;

				state.error = payload?.data?.message || "Batch generation failed";
				state.isGenerating = false;
				state.currentStage = null;
				state.currentStageLabel = null;
			}
		);
	},
});

export const {
	setGenerating,
	hydrateGenerationPhase,
	setStageUpdate,
	setGenerationQueued,
	setGenerationResult,
	setGenerationError,
	setFreshStartOverride,
	setFrameworkOverrides,
	clearFrameworkOverrides,
	applyCompanyGenerationUpdate,
	markJobReviewed,
	resetGeneration,
} = copywritingOrchestrationSlice.actions;

export const copywritingOrchestrationReducer = copywritingOrchestrationSlice.reducer;
export const copywritingOrchestrationSelector = (state: RootState) =>
	state[copywritingOrchestrationSlice.name];

/** All live jobs, queue-ordered (oldest first) — the AI queue pill's rows (P6 D7). */
export const selectGenerationJobs = createSelector(
	(state: RootState) => state[copywritingOrchestrationSlice.name].jobsByProspectId,
	(jobsByProspectId): GenerationJob[] =>
		Object.values(jobsByProspectId).sort((a, b) =>
			(a.queuedAt ?? "").localeCompare(b.queuedAt ?? "")
		)
);

/** The live job of one prospect — the per-card / per-row AI state (P6 D2/D3). */
export const selectJobByProspectId = (
	state: RootState,
	prospectId: number | null | undefined
): GenerationJob | undefined =>
	prospectId != null
		? state[copywritingOrchestrationSlice.name].jobsByProspectId[prospectId]
		: undefined;
